// src/eden/core/responseContract.js
// Eden Response Contract (v1)
// Standard shape for Eden outputs across UI, voice, reports, and automations.

import { normalizeSpeechAuthorization } from "../../voice/speechAuthorization";

import { normalizeEdenVisualizations } from "./edenVisualizationValidator";

export const EDEN_RESPONSE_VERSION = "v1";

/**
 * v1 response schema (conceptual):
 * {
 *   version: "v1",
 *   reply: string,                 // human readable main answer
 *   citations?: Array<{            // metric-based citations, not web citations
 *     source: "ai_kpi_snapshots" | "looker" | "meta" | "tiktok" | "system",
 *     label: string,              // e.g. "CTR WoW", "Spend MTD"
 *     value?: string | number,
 *     dateRange?: { start: string, end: string },
 *     notes?: string
 *   }>,
 *   insights?: Array<{
 *     title: string,
 *     detail: string,
 *     severity?: "info" | "warn" | "critical",
 *     metricDeltas?: Array<{ metric: string, delta: number, from?: number, to?: number }>
 *   }>,
 *   recommendations?: Array<{
 *     action: string,              // human readable action
 *     rationale: string,           // why
 *     expectedImpact?: string,
 *     confidence?: "low"|"medium"|"high",
 *     execute?: {                  // action mapping hook (later)
 *       type: string,              // e.g. "PAUSE_ADSET", "REALLOCATE_BUDGET"
 *       params: Record<string, any>
 *     }
 *   }>,
 *   followUps?: string[],          // suggested next questions
 *   ui?: {                         // optional UI helpers
 *     highlight?: string[],        // metric keys to highlight
 *     openPage?: string,           // route/page hint
 *   },
 *   trace?: {                      // optional debugging (superadmin only)
 *     requestId?: string,
 *     timingMs?: number
 *   }
 * }
 */

export function normalizeEdenResponse(raw) {
  // Accept raw text, {reply}, or full object
  if (!raw) {
    return {
      version: EDEN_RESPONSE_VERSION,
      reply: "No response received.",
      message: { role: "assistant", content: "No response received." },
      answerType: "general_answer",
      confidence: "low",
      status: "error",
      context: normalizeEdenContext(null),
      evidence: [],
      visualizations: [],
      actions: [],
    };
  }

  if (typeof raw === "string") {
    return {
      version: EDEN_RESPONSE_VERSION,
      reply: raw,
      message: { role: "assistant", content: raw },
      answerType: "general_answer",
      confidence: "medium",
      status: "ok",
      context: normalizeEdenContext(null),
      evidence: [],
      visualizations: [],
      actions: [],
    };
  }

  const reply = raw.reply || raw.message?.content || raw.message || raw.text || "";
  const content = String(reply || "").trim() || "No reply provided.";
  const status = normalizeEdenStatus(raw);

  return {
    speechAuthorization: normalizeSpeechAuthorization(raw.speechAuthorization, content),
    version: raw.version || EDEN_RESPONSE_VERSION,
    reply: content,
    message: {
      role: "assistant",
      content,
    },
    answerType: normalizeEdenAnswerType(raw, status),
    confidence: normalizeEdenConfidence(raw, status),
    status,
    context: normalizeEdenContext(raw.context),
    evidence: normalizeEdenEvidence(raw),
    visualizations: normalizeEdenVisualizations(raw.visualizations),
    actions: normalizeEdenActions(raw.actions),
    citations: Array.isArray(raw.citations) ? raw.citations : [],
    insights: Array.isArray(raw.insights) ? raw.insights : [],
    recommendations: Array.isArray(raw.recommendations) ? raw.recommendations : [],
    followUps: Array.isArray(raw.followUps) ? raw.followUps : [],
    ui: raw.ui && typeof raw.ui === "object" ? raw.ui : {},
    // Governed planner change proposed in chat (campaign start date). Passed
    // through as data; eden/core/edenGovernedChange.js validates it before
    // anything is shown or called.
    governedChange:
      raw.governedChange && typeof raw.governedChange === "object" && !Array.isArray(raw.governedChange)
        ? raw.governedChange
        : null,
    trace: raw.trace && typeof raw.trace === "object" ? raw.trace : {},
  };
}

const ANSWER_TYPES = new Set([
  "performance_summary",
  "recommendation",
  "investigation",
  "governance_explanation",
  "proposal_preview",
  "data_limitation",
  "general_answer",
  "governed_change_proposal",
  "governed_change_blocked",
]);
const CONFIDENCE_LEVELS = new Set(["high", "medium", "low"]);
const RESPONSE_STATUSES = new Set(["ok", "insufficient_data", "needs_clarification", "unsupported", "error"]);

function normalizeEdenStatus(raw = {}) {
  if (RESPONSE_STATUSES.has(raw.status)) return raw.status;
  if (raw.error) return "error";
  return "ok";
}

function normalizeEdenAnswerType(raw = {}, status = "ok") {
  if (ANSWER_TYPES.has(raw.answerType)) return raw.answerType;
  if (status === "insufficient_data" || status === "unsupported") return "data_limitation";
  return "general_answer";
}

function normalizeEdenConfidence(raw = {}, status = "ok") {
  if (CONFIDENCE_LEVELS.has(raw.confidence)) return raw.confidence;
  if (status === "error" || status === "unsupported") return "low";
  return "medium";
}

function normalizeEdenContext(context) {
  const source = context && typeof context === "object" && !Array.isArray(context) ? context : {};
  const dateRange = source.dateRange && typeof source.dateRange === "object" ? source.dateRange : {};
  return {
    advertiserId: source.advertiserId ?? source.advertiser?.id ?? null,
    advertiserName: source.advertiserName ?? source.advertiser?.name ?? null,
    platforms: Array.isArray(source.platforms) ? source.platforms : [],
    campaignIds: Array.isArray(source.campaignIds) ? source.campaignIds : [],
    dateRange: {
      start: dateRange.start || null,
      end: dateRange.end || null,
    },
    constraints: Array.isArray(source.constraints) ? source.constraints : [],
  };
}

function normalizeEvidenceItem(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const dateRange = item.dateRange && typeof item.dateRange === "object" ? item.dateRange : {};
  const source = String(item.source || "").trim();
  const label = String(item.label || "").trim();
  if (!source && !label) return null;
  return {
    source: source || "system",
    label: label || source || "Evidence",
    metricKey: item.metricKey ?? item.metric_key ?? null,
    windowStart: item.windowStart ?? item.window_start ?? dateRange.start ?? null,
    windowEnd: item.windowEnd ?? item.window_end ?? dateRange.end ?? null,
    sampleSize: item.sampleSize ?? item.sample_size ?? null,
  };
}

function normalizeEdenEvidence(raw = {}) {
  const source = Array.isArray(raw.evidence)
    ? raw.evidence
    : Array.isArray(raw.citations)
    ? raw.citations
    : [];
  return source.map(normalizeEvidenceItem).filter(Boolean).slice(0, 20);
}

function isJsonSafeValue(value, depth = 0, seen = new Set()) {
  if (depth > 4) return false;
  if (value == null) return true;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return typeof value !== "number" || Number.isFinite(value);
  }
  if (typeof value === "function" || typeof value === "symbol" || typeof value === "bigint") return false;
  if (typeof value !== "object") return false;
  if (seen.has(value)) return false;
  seen.add(value);
  if (Array.isArray(value)) return value.length <= 20 && value.every((item) => isJsonSafeValue(item, depth + 1, seen));
  return Object.entries(value).every(([key, item]) => {
    if (/^on[A-Z]|execute|handler|callback|dangerouslySetInnerHTML/i.test(key)) return false;
    return isJsonSafeValue(item, depth + 1, seen);
  });
}

function normalizeEdenActions(actions) {
  if (!Array.isArray(actions)) return [];
  return actions
    .filter((item) => item && typeof item === "object" && !Array.isArray(item) && isJsonSafeValue(item))
    .map((item) => ({ ...item, executionStatus: item.executionStatus || "not_executed" }))
    .slice(0, 10);
}
